import { isEmpty } from 'lodash';
import React, { forwardRef, useEffect, useState, useImperativeHandle, useRef } from 'react';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import Loader from '../Utility/Loader/Loader';
import { BombiixDropzone } from '../BombiixDropzone';

const uploadingPdforImage = (fromPt, fromPO, onlypdf, others, purchaseOrderFiles, isDragActive) => {
  // Guard against non-function arguments to prevent runtime TypeError
  const renderOnlyPdf = typeof onlypdf === 'function' ? onlypdf : () => null;
  const renderOthers = typeof others === 'function' ? others : () => null;
  const renderPOFiles = typeof purchaseOrderFiles === 'function' ? purchaseOrderFiles : () => null;

  if (fromPt) {
    return renderOnlyPdf(isDragActive);
  }
  if (fromPO) {
    return renderPOFiles(isDragActive);
  }
  return renderOthers(isDragActive);
};
const uploadPdfForTesting = (onlypdf, isDragActive) => {
  return onlypdf(isDragActive);
};

// eslint-disable-next-line no-empty-pattern
const UploadFileAndDocument = forwardRef(
  ({ acceptedFileExtenstion, showTitleField, fromPt, hideErrors = () => {}, showLoader, clearChild, setFileNames = () => {}, fromPO, variant = 'default' }, ref) => {
    const fileInputRef = useRef(null);
    const [error, setError] = useState({
      fileName: '',
      document: '',
      sizeError: '',
      invalidextExtension: '',
      error: '',
    });
    const [files, setFiles] = useState([]);
    const [supportingDocuments, setSupportingDocuments] = useState({
      fileName: '',
      document: '',
    });

    const objectAssignment = (file) => {
      return Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
    };

    useEffect(() => {
      if (clearChild) {
        setFiles([]);
        setSupportingDocuments({ fileName: '', document: '' });
      }
    }, [clearChild]);
    const { t } = useTranslation('common');

    const onFileDrop = (acceptedFiles, fileRejections) => {
      if (fileRejections?.length) {
        setError((oldErr) => ({
          ...oldErr,
          error: t('UPLOAD_FILE_AND_DOCUMENT.FILE_TYPE_ERROR_MESSAGE'),
          sizeError: '',
        }));
      } else if (acceptedFiles.length > 0 && fileRejections.length === 0) {
        const fileSize = (acceptedFiles[0].size / 1024 / 1024).toFixed(0);
        if (fileSize > 20) {
          setError((oldErr) => ({
            ...oldErr,
            error: t('UPLOAD_FILE_AND_DOCUMENT.SIZE_ERROR_MESSAGE'),
          }));
          setFiles([]);
          setSupportingDocuments((doc) => ({
            ...doc,
            document: '',
          }));
        } else {
          setError((prevErr) => ({
            ...prevErr,
            error: '',
            document: '',
            sizeError: '',
          }));
          if (fromPt && !isEmpty(acceptedFiles[0])) {
            hideErrors();
          }
          setSupportingDocuments((doc) => ({
            ...doc,
            document: acceptedFiles[0],
          }));
          setFiles(acceptedFiles.map((file) => objectAssignment(file)));
        }
      }
    };

    useEffect(
      () => () => {
        files.forEach((file) => {
          URL.revokeObjectURL(file.preview);
        });
      },
      [files],
    );

    useEffect(() => {
      if (fromPt || fromPO) {
        files?.forEach((fn) => {
          setFileNames(fn?.name);
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files, fromPt]);

    const filesName = files.map((file) => {
      return <span key={file.path}>{file.path}</span>;
    });

    const validateForm = () => {
      let formIsValid = true;
      const errors = { fileName: '', document: '', error: '' };

      if (showTitleField) {
        if (!supportingDocuments.fileName.replace(/\s/g, '').length) {
          formIsValid = false;
          errors.fileName = t('ERROR_MESSAGES.REQUIRED_FIELD');
          errors.sizeError = '';
        }
      }
      if (!supportingDocuments.document) {
        formIsValid = false;
        errors.sizeError = '';
        errors.error = t('UPLOAD_FILE_AND_DOCUMENT.SELECT_FILE_ERROR_MESSAGE');
      }
      setError((oldErr) => ({
        ...oldErr,
        ...errors,
      }));
      return formIsValid;
    };

    useImperativeHandle(ref, () => ({
      selectedDocument() {
        if (fromPt) {
          validateForm();
        }
        if (validateForm()) {
          return supportingDocuments;
        }
        return false;
      },
      // Allow parent components to reset files if needed
      resetFiles() {
        setFiles([]);
        setSupportingDocuments({ fileName: '', document: '' });
        setError({ fileName: '', document: '', sizeError: '', invalidextExtension: '', error: '' });
      },
    }));

    const handleChange = (event) => {
      const fieldName = event.currentTarget.name;
      setSupportingDocuments({
        ...supportingDocuments,
        [fieldName]: event.currentTarget.value,
      });
    };

    const others = (isDragActive) => {
      return (
        <div>
          {isDragActive ? (
            <p className="mb-0 f-16 font-medium">{t('UPLOAD_FILE_AND_DOCUMENT.FILE_DROP_MESSAGE')}</p>
          ) : (
            <p className="mb-0 text-center f-16 font-medium">{t('UPLOAD_FILE_AND_DOCUMENT.DEFAULT')}</p>
          )}
          <p className="text-center mb-0">{filesName}</p>
        </div>
      );
    };

    const onlypdf = (isDragActive) => {
      return (
        <div>
          {isDragActive ? (
            <p className="mb-0">{t('UPLOAD_FILE_AND_DOCUMENT.FILE_DROP_MESSAGE')}</p>
          ) : (
            <p className="mb-0 text-center text-bold">{t('UPLOAD_FILE_AND_DOCUMENT.ONLY_PDF')}</p>
          )}
          <p className="text-center mb-0 mt-2">{filesName}</p>
        </div>
      );
    };

    const purchaseOrderFiles = (isDragActive) => {
      return (
        <div>
          {isDragActive ? (
            <p className="mb-0 f-16 font-medium">{t('UPLOAD_FILE_AND_DOCUMENT.FILE_DROP_MESSAGE')}</p>
          ) : (
            <div className="text-center">
              <p className="mb-0 f-16 font-medium">{t('UPLOAD_FILE_AND_DOCUMENT.DEFAULT')}</p>
              <p className="mb-0 f-12 text-muted mt-1">
                {t('UPLOAD_DOCUMENTS.ALLOWED_EXTENSIONS')}: .xls, .xlsx, .pdf, .psd, .doc, .txt, .rar, .png, .jpeg, .jpg, .bmp, .tif, .raw, .gif, .ppt, .svg,
                .exp
              </p>
            </div>
          )}
          <p className="text-center mb-0 mt-2">{filesName}</p>
        </div>
      );
    };

    const errorsChanges = (err) => {
      return err.error ? err.error : err.sizeError;
    };

    const assetDropzone = () => (
      <BombiixDropzone
        type="file"
        accept={acceptedFileExtenstion}
        onDrop={onFileDrop}
        showExtensions
        footer={files.length > 0 ? <p className="bombiix-image-dropzone__file">{filesName}</p> : null}
      />
    );

    return showLoader ? (
      <Loader />
    ) : (
      <form noValidate>
        <div className="common-image-upload">
          {variant === 'asset' ? (
            assetDropzone()
          ) : (
            <Dropzone accept={acceptedFileExtenstion} onDrop={onFileDrop}>
              {({ getRootProps, getInputProps, isDragActive }) => (
                <section {...getRootProps({ className: 'upload-image-section p-3' })}>
                  <input {...getInputProps()} />
                  {fromPt && uploadPdfForTesting(onlypdf, isDragActive)}
                  {!fromPt && uploadingPdforImage(fromPt, fromPO, onlypdf, others, purchaseOrderFiles, isDragActive)}

                  <span className="upload-icon">
                    <span className="mt-16 d-inline-block icon-Upload-1 f-32 text-black" />
                  </span>
                </section>
              )}
            </Dropzone>
          )}

          {
            // show file error Massage
            Object.keys(error).length !== 0 && (
              <div className="d-flex align-items-center justify-content-center">
                <span className="error-message text-center w-50">{errorsChanges(error)}</span>
              </div>
            )
          }

          {showTitleField && (
            <div className="d-flex w-100 my-2">
              <div className="my-1 w-100">
                <input
                  type="text"
                  name="fileName"
                  value={supportingDocuments.name}
                  onChange={handleChange}
                  className="input-doc-type form-control"
                  placeholder={t('UPLOAD_FILE_AND_DOCUMENT.DOCUMENT_TITLE_PLACEHOLDER')}
                />
                <div className="d-flex align-items-center justify-content-center">
                  <span className="error-message  text-center w-50">{error.fileName}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    );
  },
);
UploadFileAndDocument.displayName = 'UploadFileAndDocument';
export default UploadFileAndDocument;
